import React, { useState, useEffect, useRef } from "react";
import { Modal, AppState, AppStateStatus } from "react-native";
import {
  BannerActivityType,
  getRemainingTimeForNextAd,
  getSecondsOfDay,
} from "./types";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { useAdBannerData } from "@apis/requestHistory";
import { BannerTypeAdState } from "@components/molecules/ProductTypesTab/types";
import AdsBanner from "@components/molecules/AdView/BannerAd";
import styled from "styled-components/native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { View } from "@gluestack-ui/themed";
import { PriceModes } from "@components/molecules/PriceModeCard/types";
import { setSearchResultCount } from "@redux/slices/appStateSlice";
import { useDispatch, useSelector } from "react-redux";
import Routes from "@navigations/Routes";
import { useNavigation } from "@react-navigation/native";
import { getActiveRouteName } from "@utils/analytics";
import { useTheme } from "styled-components/native";
interface AdBannerProps {
  productType: string;
  activityType: string;
  closeBanner?: any;
}

export const GlobalAdBanner = () => {
  const priceMode = useSelector(
    (state: any) => state.userInfo?.user?.price_mode
  );
  const currentAppState = useRef(AppState.currentState);
  const [showAdBanner, setShowAdBanner] = useState(false);
  const [nextAppState, setNextAppState] = useState<AppStateStatus>(
    currentAppState.current
  );
  const userInfo = useUserInfo();
  const userId = userInfo.getUserId();

  useEffect(() => {
    const handleAppStateChange = async (state: AppStateStatus) => {
      setNextAppState(state);
    };
    const subscription = AppState.addEventListener(
      "change",
      handleAppStateChange
    );
    return () => {
      subscription.remove();
    };
  }, []);

  useEffect(() => {
    if (
      currentAppState.current.match(/inactive|background/) &&
      nextAppState === "active"
    ) {
      if (
        userId &&
        priceMode === PriceModes.WHITE &&
        !showAdBanner &&
        userInfo.hasTermsAccepted()
      ) {
        setShowAdBanner(true);
      } else {
        setShowAdBanner(false);
      }
    }
    currentAppState.current = nextAppState;
  }, [nextAppState]);

  const navigation = useNavigation();
  const state = navigation.getState();
  const newRouteName = getActiveRouteName(state);
  const activeProductType = useSelector(
    (storeState: any) => storeState.appState?.activeProduct
  );

  const productBasedRoutes: string[] = [
    Routes.FilterScreen,
    Routes.FilterSplitScreen,
    Routes.FilterSearchScreen,
    Routes.PDPScreen,
    Routes.SearchResultScreen,
    Routes.FindMatchScreen,
    Routes.RequestDetailScreen,
    Routes.RequestModifyScreen,
  ];

  const isProductBasedRoute = productBasedRoutes.includes(newRouteName);

  const adProductType = isProductBasedRoute ? activeProductType : "global";

  return (
    showAdBanner &&
    !!priceMode &&
    priceMode === PriceModes.WHITE && (
      <AdBanner
        productType={adProductType}
        activityType={BannerActivityType.ReturnFromBackground}
        closeBanner={() => {
          setShowAdBanner(false);
        }}
      />
    )
  );
};

const AdBanner = ({
  productType: activeProductType,
  activityType,
  closeBanner,
}: AdBannerProps) => {
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const userInfo = useUserInfo();
  const theme = useTheme();
  const platform = theme.isLargeScreen ? "web" : "mobile";
  const mediaPurpose = theme.isMobile ? "banner" : "modal";
  const newRouteName = getActiveRouteName(navigation.getState());
  const userId = userInfo.getUserId();
  const priceMode = userInfo?.user?.price_mode;
  const searchResultCount = useSelector(
    (state: any) => state.appState?.searchResultCount
  );
  const adDelay = userInfo?.user?.feature_settings?.ad_delay ?? 0;

  const [callAdBanner, setCallAdBanner] = useState(false);

  const [bannerAdState, setBannerAdState] = useState<BannerTypeAdState>({
    adbannerVisible: false,
    adBannerApi: undefined,
    selectedProductType: "",
    selectedActivityType: activityType,
    tokenPresent: false,
  });

  const productBasedRoutes: string[] = [
    Routes.FilterScreen,
    Routes.FilterSplitScreen,
    Routes.FilterSearchScreen,
    Routes.PDPScreen,
    Routes.SearchResultScreen,
    Routes.FindMatchScreen,
    Routes.RequestDetailScreen,
    Routes.RequestModifyScreen,
  ];

  const isProductBasedRoute = productBasedRoutes.includes(newRouteName);
  const adProductType = isProductBasedRoute ? activeProductType : "global";

  const updateBannerAdState = (key: string, value: any) => {
    setBannerAdState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const saveAdBannerTime = async () => {
    try {
      const timestamp = getSecondsOfDay().toString();
      await AsyncStorage.setItem("@adBannerTime", timestamp);
    } catch (error) {
      console.error("Failed to save ad banner time:", error);
    }
  };

  const getTimestamp = async () => {
    try {
      const timestamp = await AsyncStorage.getItem("@adBannerTime");
      return timestamp ? Number(timestamp) : null;
    } catch (error) {
      console.error("Failed to get timestamp:", error);
      return null;
    }
  };

  const closeAdBanner = () => {
    setBannerAdState((prevState) => ({
      ...prevState,
      adBannerApi: undefined,
      adbannerVisible: false,
      selectedActivityType: "",
    }));
    closeBanner();
  };

  useEffect(() => {
    const checkToken = async () => {
      try {
        const tokenPresent = Boolean(userId);
        updateBannerAdState("tokenPresent", tokenPresent);
      } catch {
        updateBannerAdState("tokenPresent", false);
      }
    };

    checkToken();
  }, [userId]);

  useEffect(() => {
    getTimestamp()
      .then((timestamp) => {
        const remainingTime =
          timestamp !== null
            ? getRemainingTimeForNextAd(timestamp, adDelay)
            : 0;
        if (remainingTime <= 0) {
          setCallAdBanner(true);
        }
      })
      .catch((error) => {
        console.error("Error fetching timestamp:", error);
      });
  }, [activityType, adProductType]);

  const adBannerDataApi = useAdBannerData(
    adProductType,
    priceMode === PriceModes.WHITE &&
      !bannerAdState.adbannerVisible &&
      !!userId &&
      callAdBanner,
    priceMode,
    activityType,
    platform,
    mediaPurpose
  );

  useEffect(() => {
    const ad = adBannerDataApi.data?.data?.response?.body?.ad;
    setCallAdBanner(false);
    if (
      ad &&
      priceMode === PriceModes.WHITE &&
      !bannerAdState.adbannerVisible &&
      bannerAdState.tokenPresent
    ) {
      getTimestamp()
        .then((timestamp) => {
          if (
            !(
              timestamp !== null &&
              getRemainingTimeForNextAd(timestamp, adDelay) > 0
            )
          ) {
            if (
              searchResultCount === 0
                ? true
                : searchResultCount ===
                  (userInfo.user.feature_settings.ad_timing ?? 0)
            ) {
              saveAdBannerTime();
              dispatch(setSearchResultCount(0));
              updateBannerAdState("adBannerApi", ad);
              updateBannerAdState("adbannerVisible", true);
            } else if (
              searchResultCount >
              (userInfo.user.feature_settings.ad_timing ?? 0)
            ) {
              dispatch(setSearchResultCount(0));
            }
          }
        })
        .catch(() => {});
    }
  }, [
    adBannerDataApi.data,
    priceMode,
    bannerAdState.adbannerVisible,
    bannerAdState.tokenPresent,
    searchResultCount,
    adProductType,
  ]);

  useEffect(() => {
    if (
      adBannerDataApi.data &&
      activityType === BannerActivityType.ReturnFromBackground
    ) {
      const ad = adBannerDataApi.data?.data?.response?.body?.ad;
      updateBannerAdState(
        "selectedActivityType",
        BannerActivityType.ReturnFromBackground
      );
      getTimestamp()
        .then((timestamp) => {
          if (timestamp && getRemainingTimeForNextAd(timestamp, adDelay) <= 0) {
            saveAdBannerTime();
            updateBannerAdState("adBannerApi", ad);
            updateBannerAdState("adbannerVisible", true);
          } else if (!timestamp) {
            updateBannerAdState("adBannerApi", ad);
            updateBannerAdState("adbannerVisible", true);
            saveAdBannerTime();
          }
        })
        .catch(() => {});
    }
  }, [adBannerDataApi.data]);

  return (
    <View>
      {bannerAdState.adbannerVisible &&
        bannerAdState.adBannerApi?.media_type && (
          <Modal
            animationType="none"
            transparent={true}
            visible={bannerAdState.adbannerVisible}
            supportedOrientations={["landscape", "portrait"]}
          >
            <CenteredView>
              <ModalView softShadow="1">
                <AdsBanner
                  adsData={bannerAdState.adBannerApi}
                  onClose={closeAdBanner}
                />
              </ModalView>
            </CenteredView>
          </Modal>
        )}
    </View>
  );
};

const CenteredView = styled.View<any>`
  flex: 1;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme["over-01"]};
`;

const ModalView = styled(View)`
  border-radius: 22px;
  width: 100%;
  height: 100%;
`;

export default AdBanner;
